import React from 'react';
import { Import as ImportIcon } from 'lucide-react';
import { AddItemButton } from './AddItemButton';
import { EmptyStateAddButton } from './EmptyStateAddButton';
import { EmptyStateSecondaryButton } from './EmptyStateSecondaryButton';
import { useLocale } from '../contexts/LocaleContext';
import { useFeatureFlag } from '../hooks/use-feature-flag';
import { FEATURE_FLAG_SIMPLE_DOCUMENT_IMPORT, FEATURE_FLAG_WIZARD_IMPORT_BUTTON } from '../constants/feature-flags';

interface DeskProjectActionsProps {
  disabled: boolean;
  isCreating: boolean;
  isImporting: boolean;
  onCreate: () => void;
  onImportFile: () => void;
  onWizardImport: () => void;
  variant?: 'header' | 'empty';
}

export const DeskProjectActions: React.FC<DeskProjectActionsProps> = ({
  disabled,
  isCreating,
  isImporting,
  onCreate,
  onImportFile,
  onWizardImport,
  variant = 'header',
}) => {
  const { t } = useLocale();
  const simpleDocumentImportEnabled = useFeatureFlag(FEATURE_FLAG_SIMPLE_DOCUMENT_IMPORT);
  const wizardImportButtonEnabled = useFeatureFlag(FEATURE_FLAG_WIZARD_IMPORT_BUTTON);

  if (variant === 'empty') {
    return (
      <>
        {simpleDocumentImportEnabled && (
          <EmptyStateSecondaryButton onClick={onImportFile} disabled={disabled || isImporting}>
            {isImporting ? t('importing') : t('import_document')}
          </EmptyStateSecondaryButton>
        )}
        {wizardImportButtonEnabled && (
          <EmptyStateSecondaryButton onClick={onWizardImport} disabled={disabled}>
            <ImportIcon className="h-4 w-4" />
            {t('import_document')}
          </EmptyStateSecondaryButton>
        )}
        <EmptyStateAddButton onClick={onCreate} disabled={disabled || isCreating}>
          {isCreating ? t('creating') : t('create_new_document')}
        </EmptyStateAddButton>
      </>
    );
  }

  return (
    <>
      {simpleDocumentImportEnabled && (
        <AddItemButton
          onClick={onImportFile}
          disabled={disabled}
          loading={isImporting}
          label={t('import_document')}
          loadingLabel={t('importing')}
        />
      )}
      {wizardImportButtonEnabled && (
        <AddItemButton
          onClick={onWizardImport}
          disabled={disabled}
          label={t('import_document')}
          icon={<ImportIcon className="h-4 w-4" />}
        />
      )}
      <AddItemButton
        onClick={onCreate}
        disabled={disabled}
        loading={isCreating}
        label={t('create_new_document')}
        loadingLabel={t('creating')}
      />
    </>
  );
};
